import {GuidelineModel} from "../domain/guidelineModel";
import {GuidelineType} from "../../definitions/types";

export class ProductInfoAndInstallationModel {
    private readonly _instructions: GuidelineModel[];
    private readonly _universal: GuidelineModel[];
    private readonly _warranties: GuidelineModel[];

    constructor(public readonly media: {
                    guidelines: {
                        instructions: GuidelineType[],
                        universal: GuidelineType[],
                        warranty: GuidelineType[]
                    }
                },
                public readonly primaryMedia: {
                    guidelines: {
                        instructions: GuidelineType[],
                        universal: GuidelineType[],
                        warranty: GuidelineType[]
                    }
                }) {
        this._instructions = [
            ...media.guidelines.instructions.map(guideline => new GuidelineModel(guideline)),
            ...primaryMedia.guidelines.instructions.map(guideline => new GuidelineModel(guideline))
        ].filter((instruction, index, self) => index === self.findIndex((t) => t.url === instruction.url));
        this._universal = [
            ...media.guidelines.universal.map(guideline => new GuidelineModel(guideline)),
            ...primaryMedia.guidelines.universal.map(guideline => new GuidelineModel(guideline))
        ].filter((universal, index, self) => index === self.findIndex((t) => t.url === universal.url));
        this._warranties = [
            ...media.guidelines.warranty.map(guideline => new GuidelineModel(guideline)),
            ...primaryMedia.guidelines.warranty.map(guideline => new GuidelineModel(guideline))
        ].filter((warranty, index, self) => index === self.findIndex((t) => t.url === warranty.url));
    }

    /**
     * @method resources
     * @description Returns all resources (instructions, universal and warranties) for the product in a single array.
     * @returns {GuidelineModel[]}
     */
    public get resources(): GuidelineModel[] {
        return [
            ...this._instructions,
            ...this._universal,
            ...this._warranties
        ];
    }

    /**
     * @method resourceCount
     * @description Returns the total number of resources (instructions, universal and warranties) for the product.
     * @returns {number}
     */
    public get resourceCount(): number {
        return this._instructions.length + this._universal.length + this._warranties.length;
    }

    /**
     * @method hasResources
     * @description Returns true if the product has any resources (instructions or warranties).
     * @returns {boolean}
     */
    public get hasResources(): boolean {
        return this.resourceCount > 0;
    }

    /**
     * @method instructions
     * @description Returns all instructions for the product in an array.
     * @returns {GuidelineModel[]}
     */
    public get instructions(): GuidelineModel[] {
        return this._instructions;
    }

    /**
     * @method instructionsCount
     * @description Returns the total number of instructions for the product.
     * @returns {number}
     */
    public get instructionsCount(): number {
        return this._instructions.length;
    }

    /**
     * @method hasInstructions
     * @description Returns true if the product has any instructions.
     * @returns {boolean}
     */
    public get hasInstructions(): boolean {
        return this._instructions.length > 0;
    }

    /**
     * @method warranties
     * @description Returns all warranties for the product in an array.
     * @returns {GuidelineModel[]}
     */
    public get warranties(): GuidelineModel[] {
        return this._warranties;
    }

    /**
     * @method warrantiesCount
     * @description Returns the total number of warranties for the product.
     * @returns {number}
     */
    public get warrantiesCount(): number {
        return this._warranties.length;
    }

    /**
     * @method hasWarranties
     * @description Returns true if the product has any warranties.
     * @returns {boolean}
     */
    public get hasWarranties(): boolean {
        return this._warranties.length > 0;
    }

    /**
     * @property universalGuidelines
     * @description Returns all universal guidelines for the product in an array.
     * @returns {GuidelineModel[]}
     */
    public get universalGuidelines(): GuidelineModel[] {
        return this._universal;
    }

    /**
     * @property universalGuidelinesCount
     * @description Returns the total number of universal guidelines for the product.
     * @returns {number}
     */
    public get universalGuidelinesCount(): number {
        return this._universal.length;
    }

    /**
     * @property hasUniversalGuidelines
     * @description Returns true if the product has any universal guidelines.
     * @returns {boolean}
     */
    public get hasUniversalGuidelines(): boolean {
        return this._universal.length > 0;
    }

    /**
     * @method title
     * @description Returns the title for the resources section of the product page.
     * @returns {string}
     */
    public get title(): string {
        if (this.hasInstructions && this.hasWarranties) return 'Manuals & Resources';
        if (this.hasInstructions) return 'Manuals';
        return 'Resources';
    }
}